import { formatPhone } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../components/ui/alert.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Field, Input } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { useToast } from '../../lib/toast.tsx';

interface AgencySettings {
  name: string;
  publicPhone: string | null;
}

const KEY = ['settings', 'agency'] as const;

/** Agency details shown to clients (today, the general phone/WhatsApp). */
export function AgencyPage() {
  const settings = useQuery({
    queryKey: KEY,
    queryFn: () => api<AgencySettings>('/settings/agency'),
  });
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Inmobiliaria</h1>
        <p className="text-sm text-muted-foreground">Datos que ven tus clientes.</p>
      </div>
      {settings.isPending ? (
        <Loading />
      ) : settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : (
        <PhoneCard settings={settings.data} />
      )}
    </div>
  );
}

function PhoneCard({ settings }: { settings: AgencySettings }) {
  const client = useQueryClient();
  const notify = useToast();
  const [value, setValue] = useState(settings.publicPhone ? formatPhone(settings.publicPhone) : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      client.setQueryData(
        KEY,
        await api<AgencySettings>('/settings/agency', {
          method: 'PATCH',
          body: { publicPhone: value },
        }),
      );
      notify('Teléfono general guardado.', 'success');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-semibold">Teléfono y WhatsApp general</h2>
        <p className="text-sm text-muted-foreground">
          Aparece en las fichas compartidas de {settings.name} cuando el agente no tiene teléfono de
          trabajo en «Mi cuenta».
        </p>
      </div>
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="flex-1">
          <Field label="Teléfono general" error={error ?? undefined}>
            {(a11y) => (
              <Input
                {...a11y}
                type="tel"
                inputMode="tel"
                placeholder="2222 3333"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Button type="submit" variant="secondary" loading={busy}>
          Guardar
        </Button>
      </form>
    </Card>
  );
}
